import React, { useEffect, useMemo, useRef, useState } from 'react'
import Highlight from './Highlight'
import Sidebar from './Sidebar'
import GenericContent from './GenericContent'
import Header from './Header'
import InvalidatedCard from './InvalidatedCard'
import SectionCard from './SectionCard'
import SuggestedResources from './SuggestedResources'
import Transcript, {
  TranscriptFiltersBar,
  buildFlagOptions,
  type Message as TranscriptMessage,
  type QuestionAsked,
} from './Transcript'
import ProfileHeader from './micro-components/ProfileHeader'
import { TableSection } from './micro-components/TableSection'
import {
  getSummaryText,
  isCheatScoreHigh,
  normalizeProfessionalExperience,
  normalizeRows,
  normalizeScreening,
} from './report.utils'

const Report: React.FC<{
  report: any
}> = ({ report }) => {
  const [activeSection, setActiveSection] = useState<string>('summary')
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [transcriptFlagFilter, setTranscriptFlagFilter] = useState<string>('All')
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState<string>('')

  const data = report?.data ?? report ?? {}
  const profileData = data.profile ?? report?.profile
  console.log('profileData', profileData);
  const hasProfile = Boolean(profileData)

  const summaryText = getSummaryText(data.summary)
  const performanceHighlights = data.performance_highlights || data?.performanceHighlights
  const evaluationRows = normalizeRows(data.evaluation)
  const technicalRows = normalizeRows(data.technical_proficiency || data.technicalProficiency)
  const skillRows = normalizeRows(data.skill_proficiency || data.skillProficiency)
  const problemSolvingRows = normalizeRows(data.problem_solving_skills || data.problemSolvingSkills)
  const softSkillRows = normalizeRows(data.soft_skills || data.softSkills)
  const conceptualRows = normalizeRows(data.conceptual_breakdown?.concepts)
  const professionalExperienceRows = normalizeProfessionalExperience(data.professional_experience)
  const screeningRows = normalizeScreening(data.screening_questions)
  const responseFeedbackRows = normalizeRows(
    Array.isArray(data.response_feedback) && data.response_feedback.length ? data.response_feedback : [],
  )
  const resourcesStatus =
    data.resourcesStatus ||
    (typeof data.resources === 'object' && !Array.isArray(data.resources)
      ? data.resources?.status
      : undefined) ||
    (typeof report?.resources === 'object' && !Array.isArray(report?.resources)
      ? (report as any).resources?.status
      : undefined)
  const suggestedResources =
    Array.isArray(data.resources)
      ? data.resources
      : Array.isArray((data.resources as any)?.resources)
        ? (data.resources as any).resources
        : Array.isArray((data.resources as any)?.data?.resources)
          ? (data.resources as any).data.resources
          : Array.isArray((report as any)?.resources?.data?.resources)
            ? (report as any).resources.data.resources
            : []

  const transcriptMessages: TranscriptMessage[] = Array.isArray(data.transcript?.messages)
    ? data.transcript.messages
    : Array.isArray(data.messages)
      ? data.messages
      : Array.isArray(data.chat_history)
        ? data.chat_history
        : []

  const transcriptQuestions: QuestionAsked[] = Array.isArray(data.questionsAsked) ? data.questionsAsked : []

  const transcriptUsername =
    data.profile?.username || report?.profile?.username || data.username || report?.username || ''
  const isSharedReport = Boolean(data?.isSharedReport || report?.isShared || report?.shared)
  const hasTranscript = transcriptMessages.length > 0 || transcriptQuestions.length > 0
  const transcriptFlagOptions = useMemo(
    () => ['All', ...buildFlagOptions(transcriptQuestions)],
    [transcriptQuestions],
  )

  const videoUrl =
    report?.recording?.mergedUrl ||
    data.s3_recording_url?.[0] ||
    report?.s3_recording_url?.[0]

  const cheatScore = data.cheat_score ?? data.cheatScore
  const cheatSummary = data.cheat_summary ?? data.cheatSummary
  const cheatDetections = Array.isArray(data.cheat_detections) ? data.cheat_detections : []
  const isCheatedInterview =
    data.interview_status === 'cheated' || isCheatScoreHigh(cheatScore)

  const isVideoProcessing =
    report?.recording?.statusOfRecording === 'in_progress' ||
    (!videoUrl && Array.isArray(data.s3_recording_url) && data.s3_recording_url.length > 1)

  const showSummary =
    !isCheatedInterview && Boolean(summaryText || performanceHighlights || hasProfile)
  const showVideo = Boolean(videoUrl || isVideoProcessing)
  const showTechnical = !isCheatedInterview && technicalRows.length > 0
  const showSkill = !isCheatedInterview && skillRows.length > 0
  const showProblemSolving = !isCheatedInterview && problemSolvingRows.length > 0
  const showSoftSkills = !isCheatedInterview && softSkillRows.length > 0
  const showConceptual = !isCheatedInterview && conceptualRows.length > 0
  const showEvaluation = !isCheatedInterview && evaluationRows.length > 0
  const showProfessionalExp = !isCheatedInterview && professionalExperienceRows.length > 0
  const showScreening = !isCheatedInterview && screeningRows.length > 0
  const showResponseFeedback =
    !isCheatedInterview && (responseFeedbackRows.length > 0 || hasTranscript)
  const showCheatAnalysis = Boolean(
    cheatSummary || cheatDetections.length || cheatScore !== undefined
  )
  const showResources =
    !isCheatedInterview &&
    !isSharedReport &&
    (suggestedResources.length > 0 || Boolean(resourcesStatus))

  const navItems = useMemo(
    () =>
      [
        { title: 'Summary', anchor: 'summary', show: showSummary },
        { title: 'Interview Invalidated', anchor: 'interview-invalidated', show: isCheatedInterview },
        { title: 'Interview Video', anchor: 'video', show: showVideo },
        { title: 'Technical Proficiency', anchor: 'technical-proficiency', show: showTechnical },
        { title: 'Skill Proficiency', anchor: 'skill-proficiency', show: showSkill },
        { title: 'Problem Solving', anchor: 'problem-solving', show: showProblemSolving },
        { title: 'Soft Skills', anchor: 'soft-skills', show: showSoftSkills },
        { title: 'Conceptual Breakdown', anchor: 'conceptual-breakdown', show: showConceptual },
        { title: 'Evaluation', anchor: 'evaluation', show: showEvaluation },
        { title: 'Professional Experience', anchor: 'professional-exp', show: showProfessionalExp },
        { title: 'Screening', anchor: 'screening', show: showScreening },
        { title: 'Transcript', anchor: 'response-feedback', show: showResponseFeedback },
        { title: 'Cheat Analysis', anchor: 'cheat-analysis', show: showCheatAnalysis },
        { title: 'Suggested Resources', anchor: 'suggested-resources', show: showResources },
      ].filter((item) => item.show),
    [
      showSummary,
      isCheatedInterview,
      showVideo,
      showTechnical,
      showSkill,
      showProblemSolving,
      showSoftSkills,
      showConceptual,
      showEvaluation,
      showProfessionalExp,
      showScreening,
      showResponseFeedback,
      showCheatAnalysis,
      showResources,
    ],
  )

  useEffect(() => {
    const firstAnchor = navItems[0]?.anchor
    if (firstAnchor && !navItems.some((item) => item.anchor === activeSection)) {
      setActiveSection(firstAnchor)
    }
  }, [navItems, activeSection])

  useEffect(() => {
    if (!navItems.length) return
    if (observerRef.current) observerRef.current.disconnect()

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
        if (visible[0]) {
          setActiveSection(visible[0].target.id)
        }
      },
      {
        root: null,
        rootMargin: '-35% 0px -35% 0px',
        threshold: [0.2, 0.4, 0.6, 0.8, 1],
      },
    )

    navItems.forEach((section) => {
      const el = document.getElementById(section.anchor)
      if (el) observer.observe(el)
    })

    observerRef.current = observer
    return () => observer.disconnect()
  }, [navItems])

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveSection(id)
    }
  }

  const mobileNavItems = navItems
  const mobileNavRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  console.log('report data', report);

  useEffect(() => {
    const activeEl = mobileNavRefs.current[activeSection]
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [activeSection])

  const MobileNav: React.FC = () => (
    <div className="lg:hidden sticky top-[68px] z-30 bg-[#f6f8fb] px-3 pb-2">
      <div className="flex gap-2 overflow-x-auto overflow-y-hidden no-scrollbar pr-1">
        {mobileNavItems.map((item) => {
          const isActive = activeSection === item.anchor
          return (
            <button
              key={item.anchor}
              ref={(el) => {
                mobileNavRefs.current[item.anchor] = el
              }}
              onClick={() => scrollToSection(item.anchor)}
              className={`relative whitespace-nowrap px-4 py-2 text-sm font-medium transition rounded-full border ${
                isActive
                  ? 'bg-[#0F172A] text-white border-[#0F172A]'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              {item.title}
              {isActive ? (
                <span className="absolute left-4 right-4 -bottom-[9px] h-[3px] rounded-full bg-[#0F172A]" />
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className="w-full bg-[#f6f8fb] min-h-screen">
      <div className="sticky top-0 z-30 bg-[#f6f8fb]">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-3">
          <Header />
        </div>
      </div>

      {mobileNavItems.length ? <MobileNav /> : null}

      <div className="flex flex-col gap-8 sm:gap-5">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 w-full flex flex-col gap-9 md:flex-row md:items-start md:gap-8 py-6">
          <div className="w-full md:w-[25%] hidden md:block lg:sticky lg:top-[88px] lg:self-start lg:max-h-[calc(100vh-110px)] lg:overflow-auto">
            <Sidebar items={navItems} />
          </div>

          <div className="w-full md:w-[75%] flex flex-col gap-6 md:gap-7 min-w-0 h-full">
            {showSummary ? (
              <div id="summary" className="scroll-mt-28 flex flex-col gap-4">
                {hasProfile ? (
                  <ProfileHeader
                    profileData={{
                      profile_picture: profileData?.profile_picture,
                      name: profileData?.name,
                      username: profileData?.username,
                    }}
                  />
                ) : null}
                {performanceHighlights ? (
                  <Highlight
                    performance_highlights={performanceHighlights}
                    summary={summaryText}
                    rating={data?.overall_rating}
                  />
                ) : null}
              </div>
            ) : null}

            {isCheatedInterview ? (
              <div id="interview-invalidated" className="scroll-mt-28">
                <InvalidatedCard cheatScore={cheatScore} cheatSummary={cheatSummary} detections={cheatDetections} />
              </div>
            ) : null}

            {showVideo ? (
              <SectionCard
                id="video"
                title="Video Assessment"
              >
                {videoUrl ? (
                  <video className="w-full rounded-lg border bg-black max-h-[420px]" controls src={videoUrl} />
                ) : (
                  <div className="text-sm text-gray-500">Video is processing. Check back in a moment.</div>
                )}
              </SectionCard>
            ) : null}

            {showTechnical ? (
              <SectionCard
                id="technical-proficiency"
                title="Technical Proficiency"
                description="Scored competencies across core technical areas."
              >
                <TableSection title="Technical Proficiency" rows={technicalRows} />
              </SectionCard>
            ) : null}

            {showSkill ? (
              <SectionCard
                id="skill-proficiency"
                title="Skill Proficiency"
                description="Role or skill specific assessment."
              >
                <TableSection title="Skill Proficiency" rows={skillRows} />
              </SectionCard>
            ) : null}

            {showProblemSolving ? (
              <SectionCard
                id="problem-solving"
                title="Problem Solving"
                description="Structured thinking, approach, and optimization."
              >
                <TableSection title="Problem Solving" rows={problemSolvingRows} />
              </SectionCard>
            ) : null}

            {showSoftSkills ? (
              <SectionCard
                id="soft-skills"
                title="Soft Skills"
                description="Communication, collaboration, and ownership signals."
              >
                <TableSection title="Soft Skills" rows={softSkillRows} />
              </SectionCard>
            ) : null}

            {showConceptual ? (
              <SectionCard
                id="conceptual-breakdown"
                title="Conceptual Breakdown"
                description="Key concepts explored and how they were handled."
              >
                <TableSection title="Conceptual Breakdown" rows={conceptualRows} />
              </SectionCard>
            ) : null}

            {showEvaluation ? (
              <SectionCard
                id="evaluation"
                title="Evaluation"
                description="Rubric-style scoring across all sections."
              >
                <GenericContent title="Evaluation" rows={evaluationRows} />
              </SectionCard>
            ) : null}

            {/* {showProfessionalExp ? (
              <SectionCard
                id="professional-exp"
                title="Professional Experience"
                description="Experience alignment and strengths for the role."
              >
                <TableSection title="Professional Experience" rows={professionalExperienceRows} />
              </SectionCard>
            ) : null} */}

            {showScreening ? (
              <SectionCard
                id="screening"
                title="Screening"
                description="Responses to screening or short questions."
              >
                <TableSection title="Screening" rows={screeningRows} />
              </SectionCard>
            ) : null}

            {showResponseFeedback ? (
              <SectionCard
                id="response-feedback"
                title="Transcript"
                description="Per-question remarks and suggested improvements."
                actions={
                  hasTranscript ? (
                    <TranscriptFiltersBar
                      flagFilter={transcriptFlagFilter}
                      onFlagFilterChange={setTranscriptFlagFilter}
                      filterOptions={transcriptFlagOptions}
                      searchTerm={transcriptSearchTerm}
                      onSearchTermChange={setTranscriptSearchTerm}
                    />
                  ) : null
                }
              >
                {hasTranscript ? (
                  <Transcript
                    questionsAsked={transcriptQuestions}
                    messages={transcriptMessages}
                    username={transcriptUsername}
                    isSharedReport={isSharedReport}
                    hideControls
                    flagFilter={transcriptFlagFilter}
                    onFlagFilterChange={setTranscriptFlagFilter}
                    searchTerm={transcriptSearchTerm}
                    onSearchTermChange={setTranscriptSearchTerm}
                    filterOptionsOverride={transcriptFlagOptions.slice(1)}
                  />
                ) : null}
              </SectionCard>
            ) : null}

            {showCheatAnalysis ? (
              <SectionCard
                id="cheat-analysis"
                title="Cheat Analysis"
                description="Signals indicating potential malpractice during the interview."
              >
                <div className="flex flex-col gap-4">
                  {cheatScore !== undefined ? (
                    <p className="text-sm text-gray-700">
                      Score: <span className="font-semibold">{cheatScore}</span>
                    </p>
                  ) : null}
                  {cheatSummary ? <p className="text-sm text-gray-700">{cheatSummary}</p> : null}
                  {cheatDetections.length ? (
                    <ul className="list-disc pl-5 text-sm text-gray-700">
                      {cheatDetections.map((detection: any, idx: number) => (
                        <li key={idx}>{detection?.description || detection}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </SectionCard>
            ) : null}

            {showResources ? (
              <SuggestedResources
                resources={suggestedResources}
                status={resourcesStatus}
                isCheatedInterview={isCheatedInterview}
                isSharedReport={isSharedReport}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Report
